"use client";

import { usePathname, useRouter } from "../../navigation";
import { useTransition } from "react";
import type { Filters } from "../../lib/stats-report-core";
import { BTN_GHOST, INPUT } from "../ui";

type Options = { pages: { value: string; label: string }[]; sources: string[]; devices: string[]; regions: string[] };
const RANGES = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "3 months" },
  { value: "12m", label: "12 months" },
];

/** Date range and filters, kept in the page address (so a filtered view can be bookmarked or shared). */
export function StatsFilters({ filters, options }: { filters: Filters; options: Options }) {
  const router = useRouter();
  const pathname = usePathname();
  const [pending, startTransition] = useTransition();

  const apply = (changes: Partial<Filters>) => {
    const next = { ...filters, ...changes };
    const params = new URLSearchParams();
    if (next.range === "custom") {
      params.set("from", next.from);
      params.set("to", next.to);
    } else if (next.range !== "30d") params.set("range", next.range);
    for (const key of ["page", "source", "device", "region"] as const) if (next[key]) params.set(key, next[key]);
    startTransition(() => router.replace(`${pathname}${params.size ? `?${params}` : ""}`, { scroll: false }));
  };

  const select = (label: string, key: "page" | "source" | "device" | "region", choices: { value: string; label: string }[]) => (
    <label className="flex min-w-0 flex-1 basis-40 flex-col gap-1">
      <span className="text-xs font-bold">{label}</span>
      <select value={filters[key]} onChange={(e) => apply({ [key]: e.target.value })} className={INPUT}>
        <option value="">All</option>
        {filters[key] && !choices.some((c) => c.value === filters[key]) && <option value={filters[key]}>{filters[key]}</option>}
        {choices.map((c) => (
          <option key={c.value} value={c.value}>
            {c.label}
          </option>
        ))}
      </select>
    </label>
  );

  const filtered = filters.page || filters.source || filters.device || filters.region;
  return (
    <section aria-label="Filters" className="rounded-2xl border border-midnight/10 bg-sand p-4" aria-busy={pending}>
      <div className="flex flex-wrap gap-2">
        {RANGES.map((r) => (
          <button
            key={r.value}
            type="button"
            aria-pressed={filters.range === r.value}
            onClick={() => apply({ range: r.value })}
            className={`${BTN_GHOST} ${filters.range === r.value ? "border-midnight bg-midnight text-offwhite hover:text-offwhite" : "bg-offwhite"}`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-bold">From</span>
          <input type="date" value={filters.from} max={filters.to} onChange={(e) => e.target.value && apply({ range: "custom", from: e.target.value })} className={INPUT} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-bold">To</span>
          <input type="date" value={filters.to} min={filters.from} onChange={(e) => e.target.value && apply({ range: "custom", to: e.target.value })} className={INPUT} />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-3">
        {select("Page", "page", options.pages)}
        {select("Source", "source", options.sources.map((s) => ({ value: s, label: s })))}
        {select("Device", "device", options.devices.map((d) => ({ value: d, label: d[0].toUpperCase() + d.slice(1) })))}
        {select("Region", "region", options.regions.map((r) => ({ value: r, label: r })))}
      </div>
      {filtered && (
        <button type="button" onClick={() => apply({ page: "", source: "", device: "", region: "" })} className="mt-3 py-2 text-sm text-teal underline underline-offset-4">
          Clear filters
        </button>
      )}
      {pending && <p className="mt-2 text-sm text-midnight/70">Updating…</p>}
    </section>
  );
}
